import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import {
  MessageSquare, X, Loader2, Bug, Sparkles, BookOpen, AlertTriangle, Image as ImageIcon, Trash,
} from "lucide-react";
import { toast } from "sonner";

/**
 * FeedbackButton — Iter77 / Engagement.
 *
 * Floating "Send feedback" pill that lives in the Footer. Opens a modal
 * with category dropdown, free-text field, and optional screenshot upload.
 * Submits to `/api/feedback`. Admin-only retrieval at `/api/admin/feedback`.
 *
 * Why a floating pill instead of a Settings → Feedback link? Friction.
 * Users with an actual complaint will not click into Settings; they
 * close the tab. Pill keeps the channel one tap away from every page.
 */
const CATEGORIES = [
  { id: "bug", label: "Bug or broken thing", Icon: Bug, tone: "rose" },
  { id: "feature", label: "Feature request", Icon: Sparkles, tone: "cobalt" },
  { id: "content", label: "Content issue (wrong / off-topic / unsafe)", Icon: BookOpen, tone: "amber" },
  { id: "complaint", label: "Complaint", Icon: AlertTriangle, tone: "rose" },
  { id: "other", label: "Other", Icon: MessageSquare, tone: "mint" },
];

const TONE_BORDER = {
  rose: "border-rose-400/45 bg-rose-500/12 text-rose-100",
  cobalt: "border-[#0044FF]/45 bg-[#0044FF]/12 text-[#bcd1ff]",
  amber: "border-amber-400/45 bg-amber-500/12 text-amber-100",
  mint: "border-[#00FF9D]/45 bg-[#00FF9D]/10 text-[#9bffcc]",
};

export default function FeedbackButton({ floating = true }) {
  const [open, setOpen] = useState(false);
  // Iter79 — listen for `oak:open-feedback` so other surfaces (the
  // KeyFeatures carousel, error pages, "Report" rails) can open the
  // modal without owning their own button.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("oak:open-feedback", onOpen);
    return () => window.removeEventListener("oak:open-feedback", onOpen);
  }, []);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          floating
            ? "fixed bottom-20 sm:bottom-5 right-5 z-50 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#0B0E14]/85 backdrop-blur border border-white/15 hover:border-white/35 text-white text-[12px] font-display shadow-[0_8px_24px_-6px_rgba(0,0,0,0.6)] transition"
            : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-white/30 text-white/75 hover:text-white text-[11.5px] font-display transition"
        }
        data-testid="feedback-button"
        aria-label="Send feedback"
      >
        <MessageSquare size={12} />
        Feedback
      </button>
      <FeedbackModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function FeedbackModal({ open, onOpenChange }) {
  const [category, setCategory] = useState("bug");
  const [body, setBody] = useState("");
  const [shot, setShot] = useState(null); // {data_url, size}
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setCategory("bug"); setBody(""); setShot(null);
    }
  }, [open]);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Pick an image file");
      return;
    }
    if (file.size > 1_500_000) {
      toast.error("Image too large (max 1.5MB) — try a smaller crop");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setShot({ data_url: reader.result, size: file.size });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!body.trim()) { toast.error("Tell us a bit more before sending"); return; }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        category,
        body: body.trim(),
        screenshot_data_url: shot?.data_url || null,
        page_path: typeof window !== "undefined" ? window.location.pathname : "",
      });
      toast.success("Sent — thank you. We read every message.");
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B0E14]/95 border-white/10 text-white max-w-md p-0 overflow-hidden"
        data-testid="feedback-modal"
      >
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 grid place-items-center rounded-lg bg-[#0044FF]/12 border border-[#0044FF]/35">
              <MessageSquare size={14} className="text-[#9bc7ff]" />
            </div>
            <div>
              <div className="font-display text-sm tracking-tight">Send feedback</div>
              <div className="text-[10.5px] text-white/45 font-mono-oak">
                Every message reaches a real human
              </div>
            </div>
          </div>
          <button
            onClick={() => onOpenChange?.(false)}
            className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5"
            aria-label="Close"
            data-testid="feedback-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Category */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-2">What's this about?</div>
            <div className="grid grid-cols-1 gap-1.5">
              {CATEGORIES.map(({ id, label, Icon, tone }) => {
                const sel = category === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCategory(id)}
                    className={`text-left px-3 py-2 rounded-lg border flex items-center gap-2.5 text-[12.5px] transition ${
                      sel
                        ? TONE_BORDER[tone]
                        : "border-white/8 bg-white/[0.025] text-white/75 hover:bg-white/[0.05] hover:border-white/18"
                    }`}
                    data-testid={`feedback-category-${id}`}
                  >
                    <Icon size={13} className="shrink-0" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1.5">Your message</div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 4000))}
              rows={5}
              placeholder="What happened? What did you expect? Steps to reproduce help a lot for bugs."
              className="w-full text-[13px] px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.04] text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 resize-none"
              data-testid="feedback-body"
            />
            <div className="text-[10px] text-white/35 font-mono-oak mt-1 text-right">
              {body.length}/4000
            </div>
          </label>

          {/* Screenshot */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1.5">Screenshot (optional)</div>
            <AnimatePresence>
              {shot ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative rounded-lg border border-white/10 overflow-hidden"
                  data-testid="feedback-shot-preview"
                >
                  <img src={shot.data_url} alt="" className="w-full max-h-44 object-cover" />
                  <button
                    type="button"
                    onClick={() => setShot(null)}
                    className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-black/75 border border-white/15 text-white hover:bg-black"
                    aria-label="Remove screenshot"
                    data-testid="feedback-shot-clear"
                  >
                    <Trash size={12} />
                  </button>
                </motion.div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border border-dashed border-white/15 rounded-lg px-3 py-4 text-[11.5px] text-white/45 hover:text-white/75 hover:border-white/30 inline-flex items-center justify-center gap-1.5"
                  data-testid="feedback-shot-pick"
                >
                  <ImageIcon size={13} /> Attach a screenshot
                </button>
              )}
            </AnimatePresence>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPickFile}
              data-testid="feedback-shot-input"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/8 flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenChange?.(false)}
            className="text-[12px] text-white/55 hover:text-white px-3 py-2 rounded-md hover:bg-white/5"
            data-testid="feedback-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !body.trim()}
            className="btn-primary px-5 py-2 rounded-full font-display font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="feedback-submit"
          >
            {submitting ? <><Loader2 size={12} className="animate-spin" /> Sending…</> : "Send feedback"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
